import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { galleryAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Images, Plus, Trash, Eye, EyeSlash, X, Upload, Pencil, Check
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const GalleryManagementPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    title: '',
    description: '',
    isPublic: true
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await galleryAPI.getAll();
      setImages(response.data || []);
    } catch (error) {
      toast.error('Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Please select an image to upload');
      return;
    }

    setUploading(true);
    try {
      await galleryAPI.upload(
        uploadData.file,
        uploadData.title,
        uploadData.description,
        uploadData.isPublic
      );
      toast.success('Image uploaded successfully');
      setShowUploadModal(false);
      setUploadData({ file: null, title: '', description: '', isPublic: true });
      fetchImages();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleVisibility = async (image) => {
    try {
      await galleryAPI.update(image.id, { is_public: !image.is_public });
      toast.success(image.is_public ? 'Image hidden from public gallery' : 'Image visible in public gallery');
      fetchImages();
    } catch (error) {
      toast.error('Failed to update image visibility');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await galleryAPI.delete(imageId);
      toast.success('Image deleted successfully');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleUpdateImage = async () => {
    if (!editingImage) return;
    
    try {
      await galleryAPI.update(editingImage.id, {
        title: editingImage.title,
        description: editingImage.description
      });
      toast.success('Image updated successfully');
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="gallery-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl text-[#1A1614]">Gallery Management</h1>
            <p className="text-sm text-[#8A847A] mt-1">
              Upload and manage images for the public gallery
            </p>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
            data-testid="upload-image-btn"
          >
            <Plus size={18} className="mr-2" />
            Upload Image
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <p className="text-sm text-[#8A847A]">Total Images</p>
            <p className="font-heading text-2xl text-[#1A1614]">{images.length}</p>
          </div>
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <p className="text-sm text-[#8A847A]">Public Images</p>
            <p className="font-heading text-2xl text-[#1A1614]">
              {images.filter(img => img.is_public).length}
            </p>
          </div>
          <div className="bg-white border border-[#D8D2C4] p-4 rounded">
            <p className="text-sm text-[#8A847A]">Hidden Images</p>
            <p className="font-heading text-2xl text-[#1A1614]">
              {images.filter(img => !img.is_public).length}
            </p>
          </div>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : images.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {images.map(image => (
              <div
                key={image.id}
                className={`relative group bg-white border rounded overflow-hidden ${
                  image.is_public ? 'border-[#D8D2C4]' : 'border-[#8A847A] border-dashed'
                }`}
                data-testid={`gallery-item-${image.id}`}
              >
                {/* Image */}
                <div 
                  className="aspect-square cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={`${API}/api/gallery/${image.id}/download`}
                    alt={image.title || 'Gallery image'}
                    className={`w-full h-full object-cover ${!image.is_public ? 'opacity-50' : ''}`}
                  />
                </div>

                {/* Visibility Badge */}
                {!image.is_public && (
                  <div className="absolute top-2 left-2 bg-[#1A1614]/80 text-white text-xs px-2 py-1 rounded">
                    Hidden
                  </div>
                )}

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleToggleVisibility(image)}
                    className="p-2 bg-white rounded-full hover:bg-[#F5F2EB] transition-colors"
                    title={image.is_public ? 'Hide from gallery' : 'Show in gallery'}
                    data-testid={`toggle-visibility-${image.id}`}
                  >
                    {image.is_public ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => setEditingImage(image)}
                    className="p-2 bg-white rounded-full hover:bg-[#F5F2EB] transition-colors"
                    title="Edit details"
                    data-testid={`edit-image-${image.id}`}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-white rounded-full hover:bg-red-50 text-red-600 transition-colors"
                    title="Delete image"
                    data-testid={`delete-image-${image.id}`}
                  >
                    <Trash size={18} />
                  </button>
                </div>

                {/* Title */}
                {image.title && (
                  <div className="p-2 border-t border-[#D8D2C4]">
                    <p className="text-xs text-[#4A453E] truncate">{image.title}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] rounded p-12 text-center">
            <Images size={48} className="mx-auto text-[#8A847A] mb-4" />
            <h3 className="font-heading text-lg text-[#1A1614] mb-2">No Images Yet</h3>
            <p className="text-sm text-[#8A847A] mb-4">
              Upload images to display them in the public gallery.
            </p>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
            >
              <Plus size={18} className="mr-2" />
              Upload First Image
            </Button>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md" data-testid="upload-modal">
              <div className="flex items-center justify-between p-4 border-b border-[#D8D2C4]">
                <h2 className="font-heading text-lg text-[#1A1614]">Upload Image</h2>
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="text-[#8A847A] hover:text-[#1A1614]"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* File Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    uploadData.file 
                      ? 'border-[#7B1818] bg-[#7B1818]/5' 
                      : 'border-[#D8D2C4] hover:border-[#C5A059]'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  {uploadData.file ? (
                    <div>
                      <Check size={32} className="mx-auto text-[#7B1818] mb-2" />
                      <p className="text-sm text-[#1A1614] font-medium">{uploadData.file.name}</p>
                      <p className="text-xs text-[#8A847A] mt-1">Click to change</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={32} className="mx-auto text-[#8A847A] mb-2" />
                      <p className="text-sm text-[#4A453E]">Click to select an image</p>
                      <p className="text-xs text-[#8A847A] mt-1">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#4A453E] mb-1">
                    Title (optional)
                  </label>
                  <Input
                    value={uploadData.title}
                    onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter image title"
                    data-testid="upload-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#4A453E] mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter image description"
                    rows={3}
                    className="w-full px-3 py-2 border border-[#D8D2C4] rounded text-sm focus:outline-none focus:border-[#C5A059]"
                    data-testid="upload-description-input"
                  />
                </div>

                {/* Public Toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadData.isPublic}
                    onChange={(e) => setUploadData(prev => ({ ...prev, isPublic: e.target.checked }))}
                    className="w-4 h-4 text-[#7B1818] border-[#D8D2C4] rounded focus:ring-[#C5A059]"
                    data-testid="upload-public-checkbox"
                  />
                  <span className="text-sm text-[#4A453E]">Show in public gallery</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-[#D8D2C4]">
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!uploadData.file || uploading}
                  className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="preview-modal"
          >
            <button 
              className="absolute top-4 right-4 text-white hover:text-[#C5A059]"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <div className="max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <img
                src={`${API}/api/gallery/${selectedImage.id}/download`}
                alt={selectedImage.title || 'Gallery image'}
                className="max-w-full max-h-[80vh] object-contain"
              />
              {(selectedImage.title || selectedImage.description) && (
                <div className="text-center mt-4">
                  {selectedImage.title && (
                    <h3 className="font-heading text-xl text-white">{selectedImage.title}</h3>
                  )}
                  {selectedImage.description && (
                    <p className="text-[#8A847A] mt-1">{selectedImage.description}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md" data-testid="edit-modal">
              <div className="flex items-center justify-between p-4 border-b border-[#D8D2C4]">
                <h2 className="font-heading text-lg text-[#1A1614]">Edit Image Details</h2>
                <button 
                  onClick={() => setEditingImage(null)}
                  className="text-[#8A847A] hover:text-[#1A1614]"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Preview */}
                <div className="aspect-video bg-[#F5F2EB] rounded overflow-hidden">
                  <img
                    src={`${API}/api/gallery/${editingImage.id}/download`}
                    alt={editingImage.title || 'Gallery image'}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#4A453E] mb-1">Title</label>
                  <Input
                    value={editingImage.title || ''}
                    onChange={(e) => setEditingImage(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter image title"
                    data-testid="edit-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#4A453E] mb-1">Description</label>
                  <textarea
                    value={editingImage.description || ''}
                    onChange={(e) => setEditingImage(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter image description"
                    rows={3}
                    className="w-full px-3 py-2 border border-[#D8D2C4] rounded text-sm focus:outline-none focus:border-[#C5A059]"
                    data-testid="edit-description-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-[#D8D2C4]">
                <Button
                  variant="outline"
                  onClick={() => setEditingImage(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateImage}
                  className="bg-[#7B1818] hover:bg-[#5A1010] text-white"
                  data-testid="confirm-edit-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GalleryManagementPage;
